import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { RefreshFailed } from '@/components/RefreshFailed'
import { accountCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Field, Input } from '@/ui/Field/Field'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { ApiError } from '../../api/client'
import { meApi } from '../../api/workspaces'
import { useMeQuery } from '../../hooks/queries'
import { useToastStore } from '../../store/toast'
import styles from './AccountTab.module.css'

const { account, password } = accountCopy

export function AccountTab() {
  const meQuery = useMeQuery()
  const me = meQuery.data

  return (
    <Stack gap="space-24">
      <AccountSection meQuery={meQuery} />
      <PasswordSection userId={me?.user_id} />
    </Stack>
  )
}

function AccountSection({
  meQuery,
}: {
  meQuery: ReturnType<typeof useMeQuery>
}) {
  const { data: me } = meQuery
  const userId = me?.user_id
  const username = me?.username
  const email = me?.email
  const isSystemAdmin = me?.is_system_admin
  const qc = useQueryClient()
  const { addToast } = useToastStore()
  const [editingEmail, setEditingEmail] = useState(false)
  const [emailDraft, setEmailDraft] = useState('')
  const showSkeleton = useSkeletonDelay(meQuery.isPending)

  const emailMut = useMutation({
    mutationFn: () => meApi.updateEmail(emailDraft.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] })
      setEditingEmail(false)
      addToast(account.emailUpdated, 'success')
    },
    onError: () => addToast(account.emailFailed, 'error'),
  })

  return (
    <section aria-busy={meQuery.isPending}>
      <SectionHeader rule trailingSpacing="section">
        {account.heading}
      </SectionHeader>
      {meQuery.isPending ? (
        showSkeleton && <Skeleton lines={4} />
      ) : meQuery.isError && !meQuery.data ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline" wrap>
            <Text>{account.loadFailed}</Text>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void meQuery.refetch()}
            >
              {accountCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : (
        <Stack gap="space-12">
          {meQuery.isError && (
            <RefreshFailed
              message={account.refreshFailed}
              onRetry={() => void meQuery.refetch()}
            />
          )}
          <RowList hover={false} aria-label={account.heading}>
            <RowListItem>
              <Text tone="text-mute" className={styles.label}>
                {account.username}
              </Text>
              <Text>{username ?? '—'}</Text>
            </RowListItem>
            <RowListItem>
              <Text tone="text-mute" className={styles.label}>
                {account.userId}
              </Text>
              <Text variant="code" tone="text-sub">
                {userId ?? '—'}
              </Text>
            </RowListItem>
            <RowListItem>
              <Text tone="text-mute" className={styles.label}>
                {account.email}
              </Text>
              {editingEmail ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    emailMut.mutate()
                  }}
                >
                  <Inline gap="space-8">
                    <Input
                      autoFocus
                      type="email"
                      aria-label={account.email}
                      value={emailDraft}
                      onChange={(e) => setEmailDraft(e.target.value)}
                      placeholder={account.emailPlaceholder}
                      className={styles.email}
                    />
                    <Button
                      type="submit"
                      disabled={!emailDraft.trim()}
                      loading={emailMut.isPending}
                    >
                      {account.save}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingEmail(false)}
                    >
                      {account.cancel}
                    </Button>
                  </Inline>
                </form>
              ) : (
                <Inline gap="space-8" align="baseline">
                  <Text tone="text-sub">{email ?? '—'}</Text>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEmailDraft(email ?? '')
                      setEditingEmail(true)
                    }}
                  >
                    {account.edit}
                  </Button>
                </Inline>
              )}
            </RowListItem>
            {isSystemAdmin && (
              <RowListItem>
                <Text tone="text-mute" className={styles.label}>
                  {account.role}
                </Text>
                <Text>{account.systemAdmin}</Text>
              </RowListItem>
            )}
          </RowList>
        </Stack>
      )}
    </section>
  )
}

function PasswordSection({ userId }: { userId?: string }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const addToast = useToastStore((s) => s.addToast)

  const changeMutation = useMutation({
    mutationFn: () => meApi.changePassword(current, next),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setConfirm('')
      addToast(password.updated, 'success')
    },
  })

  const mismatch = confirm.length > 0 && next !== confirm
  const canSubmit =
    !!userId && current.length > 0 && next.length >= 8 && next === confirm

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (canSubmit) changeMutation.mutate()
  }

  return (
    <section>
      <SectionHeader rule trailingSpacing="section">
        {password.heading}
      </SectionHeader>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Stack gap="space-16">
          <Field label={password.current}>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <Field label={password.next}>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field
            label={password.confirm}
            error={mismatch ? password.mismatch : undefined}
          >
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </Field>

          {changeMutation.isError && (
            <Alert tone="danger">
              {changeMutation.error instanceof ApiError &&
              changeMutation.error.status === 422
                ? password.incorrect
                : password.failed}
            </Alert>
          )}

          <Inline gap="space-8">
            <Button
              variant="outline"
              type="submit"
              disabled={!canSubmit}
              loading={changeMutation.isPending}
            >
              {password.submit}
            </Button>
          </Inline>
        </Stack>
      </form>
    </section>
  )
}
